import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { content } from "../../data/content";
import { popAt } from "../../lib/confetti";
import { Reveal } from "./Reveal";

function Balloon({ item, index, onPop }) {
  const drift = index % 2 === 0 ? -8 : 8;
  return (
    <motion.button
      data-testid={`balloon-${index}`}
      onClick={(e) => {
        const x = e.clientX / window.innerWidth;
        const y = e.clientY / window.innerHeight;
        onPop(index, x, y);
      }}
      className="relative outline-none"
      initial={{ y: 0 }}
      animate={{ y: [0, -14, 0], rotate: [drift, -drift, drift] }}
      transition={{
        duration: 3 + (index % 3),
        repeat: Infinity,
        ease: "easeInOut",
        delay: index * 0.2,
      }}
      whileHover={{ scale: 1.06 }}
      whileTap={{ scale: 0.9 }}
      aria-label={`Reventar globo ${index + 1}`}
    >
      <svg width="72" height="96" viewBox="0 0 72 96" className="drop-shadow-[3px_4px_0_rgba(28,25,23,0.25)]">
        <path
          d="M36 4 C54 4 66 18 66 38 C66 58 50 74 36 78 C22 74 6 58 6 38 C6 18 18 4 36 4 Z"
          fill={item.color}
          stroke="#1C1917"
          strokeWidth="3"
        />
        <path d="M36 78 l-5 8 h10 z" fill={item.color} stroke="#1C1917" strokeWidth="2" />
        <path d="M36 86 q6 8 0 10" fill="none" stroke="#1C1917" strokeWidth="2" />
        <ellipse cx="27" cy="28" rx="6" ry="9" fill="rgba(255,255,255,0.45)" />
      </svg>
    </motion.button>
  );
}

export function Balloons() {
  const [popped, setPopped] = useState([]);
  const [reveal, setReveal] = useState(null);

  const handlePop = (index, x, y) => {
    if (popped.includes(index)) return;
    popAt(x, y);
    setPopped((p) => [...p, index]);
    setReveal({ word: content.balloons.items[index].word, id: index });
    setTimeout(() => setReveal((r) => (r && r.id === index ? null : r)), 1600);
  };

  const allPopped = popped.length === content.balloons.items.length;

  return (
    <section
      data-testid="balloons-section"
      className="relative w-full bg-blush px-5 md:px-10 py-24 md:py-32 overflow-hidden"
    >
      <div className="max-w-5xl mx-auto text-center">
        <Reveal>
          <p className="font-accent text-3xl md:text-5xl text-grape -rotate-2 mb-1">
            {content.balloons.kicker}
          </p>
          <h2 className="font-heading font-black uppercase tracking-tighter text-4xl md:text-6xl text-ink">
            {content.balloons.title}
          </h2>
          <p className="font-body font-semibold text-base md:text-xl text-ink/70 mt-3">
            {content.balloons.subtitle}
          </p>
        </Reveal>

        <div className="mt-14 flex flex-wrap justify-center items-end gap-x-6 gap-y-10 md:gap-x-12 min-h-[140px]">
          {content.balloons.items.map((item, i) =>
            popped.includes(i) ? (
              <div
                key={i}
                className="w-[72px] h-[96px] flex items-center justify-center"
              >
                <span className="font-accent text-2xl md:text-3xl text-ink/70 rotate-[-6deg]">
                  {item.word}
                </span>
              </div>
            ) : (
              <Balloon key={i} item={item} index={i} onPop={handlePop} />
            )
          )}
        </div>

        <div className="h-16 mt-6 flex items-center justify-center">
          <AnimatePresence mode="wait">
            {allPopped && (
              <motion.p
                key="done"
                data-testid="balloons-done"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                className="font-accent text-4xl md:text-6xl text-rose"
              >
                {content.balloons.done}
              </motion.p>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* floating word pop */}
      <AnimatePresence>
        {reveal && (
          <motion.span
            key={reveal.id}
            initial={{ opacity: 0, y: 0, scale: 0.6 }}
            animate={{ opacity: 1, y: -50, scale: 1 }}
            exit={{ opacity: 0 }}
            className="pointer-events-none fixed left-1/2 top-1/3 -translate-x-1/2 z-[9997] font-heading font-black text-5xl md:text-7xl text-rose"
          >
            {reveal.word}
          </motion.span>
        )}
      </AnimatePresence>
    </section>
  );
}
